/**
 * O-49: the Data portal's "report a wrong or missing answer" client and the
 * triage inbox client. Both talk to the agent API's `/api/data-issues`
 * routes: a report is stored, sent to Hunter as a Slack DM and to the data
 * platform channel, and worked in the inbox at `/data/issues`.
 */

export const DATA_ISSUE_ROUTE = '/api/data-issues';

export const DATA_ISSUE_SCHEMA = 'marsmen.data-issue.v1';

export const DATA_ISSUES_PATH = '/data/issues';
export const dataIssuePath = (issueId: string) => `${DATA_ISSUES_PATH}/${encodeURIComponent(issueId)}`;

export type DataIssueType =
  | 'wrong_number'
  | 'wrong_source'
  | 'missing_source'
  | 'stale_data'
  | 'unclear'
  | 'other';

export type DataIssueSurface = 'mcp' | 'explorer' | 'ask';
export type DataIssueArea = 'query' | 'metric' | 'ask' | 'table' | 'chat' | 'mcp' | 'other';
export type DataIssueStatus = 'new' | 'investigating' | 'resolved' | 'wont_fix';
export type DataIssueDeliveryStatus = 'delivered' | 'failed' | 'not_configured';

/** Chip order and copy for the report card. */
export const DATA_ISSUE_TYPE_OPTIONS: Array<{ value: DataIssueType; label: string }> = [
  { value: 'wrong_number', label: 'A number looks wrong' },
  { value: 'wrong_source', label: 'Wrong source' },
  { value: 'missing_source', label: 'Missing data' },
  { value: 'stale_data', label: 'Stale' },
  { value: 'unclear', label: 'Unclear' },
  { value: 'other', label: 'Other' },
];

export const DATA_ISSUE_TYPE_LABEL: Record<DataIssueType, string> = Object.fromEntries(
  DATA_ISSUE_TYPE_OPTIONS.map((option) => [option.value, option.label]),
) as Record<DataIssueType, string>;

export const DATA_ISSUE_STATUS_LABEL: Record<DataIssueStatus, string> = {
  new: 'New',
  investigating: 'Investigating',
  resolved: 'Resolved',
  wont_fix: "Won't fix",
};

export const DATA_ISSUE_AREA_LABEL: Record<DataIssueArea, string> = {
  query: 'Query editor',
  metric: 'Metric',
  ask: 'Ask',
  table: 'Table',
  chat: 'Ask Mars chat',
  mcp: 'Claude / MCP',
  other: 'Other',
};

export interface DataIssueValuesSnapshot {
  columns: string[];
  rows: string[][];
  rowCount: number | null;
}

export interface DataIssueInput {
  issueType: DataIssueType;
  area?: DataIssueArea | null;
  question: string;
  answerSummary?: string | null;
  note?: string | null;
  sql?: string | null;
  metricId?: string | null;
  tableNames?: string[];
  catalogRevision?: string | null;
  valuesSnapshot?: DataIssueValuesSnapshot | null;
  link?: string | null;
  sessionId?: string | null;
  turnId?: string | null;
}

export interface DataIssueDeliveryTarget {
  target: 'dm' | 'channel' | 'reporter_dm';
  destination: string;
  status: DataIssueDeliveryStatus;
  detail: string;
  attemptedAt: string;
}

export interface DataIssue {
  issueId: string;
  ownerEmail: string;
  reporterName: string | null;
  surface: DataIssueSurface;
  area: DataIssueArea | null;
  issueType: DataIssueType;
  question: string;
  answerSummary: string | null;
  sql: string | null;
  metricId: string | null;
  tableNames: string[];
  catalogRevision: string | null;
  valuesSnapshot: DataIssueValuesSnapshot | null;
  link: string | null;
  note: string | null;
  createdAt: string;
  status: DataIssueStatus;
  assigneeEmail: string | null;
  resolutionNote: string | null;
  fixPrUrl: string | null;
  updatedAt: string;
  updatedBy: string | null;
  resolvedAt: string | null;
  delivery: { status: DataIssueDeliveryStatus; detail: string; attemptedAt?: string };
  deliveries: DataIssueDeliveryTarget[];
  reporterNotice: { channel: 'slack_dm' | 'portal'; status: DataIssueDeliveryStatus; detail: string; at: string } | null;
  version: number;
}

export interface DataIssueEvent {
  eventId: string;
  issueId: string;
  at: string;
  actorEmail: string;
  action: 'created' | 'status' | 'assignee' | 'resolution_note' | 'fix_pr' | 'notified' | 'reporter_notified';
  from: string | null;
  to: string | null;
}

export interface DataIssueViewer {
  email: string;
  isAdmin: boolean;
}

export interface DataIssueReceipt {
  issueId: string;
  createdAt: string;
  issueType: DataIssueType;
  surface: DataIssueSurface;
  delivery: {
    status: DataIssueDeliveryStatus;
    detail: string;
  };
  deliveries: DataIssueDeliveryTarget[];
}

export class DataIssueError extends Error {
  readonly status: number | null;

  constructor(message: string, status: number | null = null) {
    super(message);
    this.name = 'DataIssueError';
    this.status = status;
  }
}

export const resolveDataIssueUrl = (
  configured: string | undefined,
  agentApiBaseUrl: string | undefined,
): string => {
  const override = configured?.trim();
  if (override) return override.replace(/\/+$/, '');
  const apiBase = agentApiBaseUrl?.trim().replace(/\/+$/, '') ?? '';
  return `${apiBase}${DATA_ISSUE_ROUTE}`;
};

export const dataIssueUrl = (): string => {
  const env = (import.meta as { env?: Record<string, string | undefined> }).env;
  return resolveDataIssueUrl(
    env?.VITE_DATA_ISSUE_URL,
    env?.VITE_AI_CENTER_AGENT_API_BASE_URL ?? env?.VITE_AGENT_API_BASE_URL,
  );
};

const truncate = (value: string, max: number) => (value.length > max ? value.slice(0, max) : value);

/** A bounded copy of the rows on screen (12 columns x 20 rows, 200 chars a cell). */
export const buildValuesSnapshot = (
  columns: string[] | null | undefined,
  rows: Array<Record<string, unknown>> | null | undefined,
  rowCount?: number | null,
): DataIssueValuesSnapshot | null => {
  if (!columns?.length || !rows?.length) return null;
  const kept = columns.slice(0, 12);
  return {
    columns: kept.map((column) => truncate(String(column), 200)),
    rows: rows.slice(0, 20).map((row) => kept.map((column) => truncate(row[column] == null ? '' : String(row[column]), 200))),
    rowCount: typeof rowCount === 'number' ? rowCount : rows.length,
  };
};

/** "We'll include: …" line for the report card. */
export const reportContextSummary = (input: DataIssueInput): string[] => {
  const parts: string[] = [];
  const tableOnly = input.area === 'table' && (input.tableNames ?? []).length > 0;
  if (input.question.trim() && !tableOnly) parts.push('question');
  if (input.sql?.trim()) parts.push('SQL');
  if (input.metricId) parts.push(`metric ${input.metricId}`);
  const tables = (input.tableNames ?? []).filter((name) => name !== input.metricId);
  if (tables.length === 1) parts.push(`table ${tables[0]}`);
  else if (tables.length > 1) parts.push(`${tables.length} tables`);
  if (input.valuesSnapshot?.rows.length) {
    parts.push(`the first ${input.valuesSnapshot.rows.length} row${input.valuesSnapshot.rows.length === 1 ? '' : 's'} you see`);
  }
  if (input.catalogRevision) parts.push(`catalog rev ${input.catalogRevision.slice(0, 7)}`);
  if (input.link) parts.push('a link to this page');
  return parts;
};

const readJson = async (response: Response) =>
  (await response.json().catch(() => null)) as Record<string, unknown> | null;

const failure = (response: Response, body: Record<string, unknown> | null, fallback: string) =>
  new DataIssueError(
    response.status === 401
      ? 'Sign in again to see reported issues.'
      : typeof body?.message === 'string'
        ? body.message
        : fallback,
    response.status,
  );

type ClientOptions = { url?: string; fetchImpl?: typeof fetch };

const send = async (
  path: string,
  init: RequestInit,
  options: ClientOptions,
  fallback: string,
): Promise<Record<string, unknown>> => {
  const fetchImpl = options.fetchImpl ?? fetch;
  let response: Response;
  try {
    response = await fetchImpl(`${options.url?.trim() || dataIssueUrl()}${path}`, {
      ...init,
      headers: { accept: 'application/json', ...(init.body ? { 'content-type': 'application/json' } : {}) },
      credentials: 'include',
    });
  } catch {
    throw new DataIssueError('The data platform API could not be reached. Try again in a moment.');
  }
  const body = await readJson(response);
  if (!response.ok || !body?.ok) throw failure(response, body, fallback);
  return body;
};

export const submitDataIssue = async (
  input: DataIssueInput,
  options: ClientOptions & { surface?: DataIssueSurface } = {},
): Promise<DataIssueReceipt> => {
  const fetchImpl = options.fetchImpl ?? fetch;
  let response: Response;
  try {
    response = await fetchImpl(options.url?.trim() || dataIssueUrl(), {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ ...input, surface: options.surface ?? 'explorer' }),
      credentials: 'include',
    });
  } catch {
    throw new DataIssueError(
      'The report could not reach the data platform API. Try again, or message Hunter in Slack.',
    );
  }
  const body = (await response.json().catch(() => null)) as
    | {
        ok?: boolean;
        message?: string;
        issue?: Pick<DataIssue, 'issueId' | 'createdAt' | 'issueType' | 'surface' | 'deliveries'>;
        delivery?: DataIssueReceipt['delivery'];
      }
    | null;
  if (!response.ok || !body?.ok || !body.issue) {
    throw new DataIssueError(body?.message ?? 'The report could not be filed.', response.status);
  }
  return {
    issueId: body.issue.issueId,
    createdAt: body.issue.createdAt,
    issueType: body.issue.issueType,
    surface: body.issue.surface,
    delivery: body.delivery ?? {
      status: 'not_configured',
      detail: 'Notification status was not returned.',
    },
    deliveries: body.issue.deliveries ?? [],
  };
};

export interface DataIssueListQuery {
  scope: 'mine' | 'all';
  status?: DataIssueStatus | '';
  type?: DataIssueType | '';
  area?: DataIssueArea | '';
}

export const listDataIssues = async (
  query: DataIssueListQuery,
  options: ClientOptions = {},
): Promise<{ issues: DataIssue[]; viewer: DataIssueViewer }> => {
  const params = new URLSearchParams({ scope: query.scope, limit: '200' });
  if (query.status) params.set('status', query.status);
  if (query.type) params.set('type', query.type);
  if (query.area) params.set('area', query.area);
  const body = await send(`?${params}`, { method: 'GET' }, options, 'Reported issues could not be loaded.');
  return { issues: (body.issues as DataIssue[]) ?? [], viewer: body.viewer as DataIssueViewer };
};

export const getDataIssue = async (
  issueId: string,
  options: ClientOptions = {},
): Promise<{ issue: DataIssue; events: DataIssueEvent[]; viewer: DataIssueViewer }> => {
  const body = await send(`/${encodeURIComponent(issueId)}`, { method: 'GET' }, options, 'That report could not be loaded.');
  return {
    issue: body.issue as DataIssue,
    events: (body.events as DataIssueEvent[]) ?? [],
    viewer: body.viewer as DataIssueViewer,
  };
};

export interface DataIssuePatch {
  status?: DataIssueStatus;
  assigneeEmail?: string | null;
  resolutionNote?: string | null;
  fixPrUrl?: string | null;
  expectedVersion?: number;
}

export const updateDataIssue = async (
  issueId: string,
  patch: DataIssuePatch,
  options: ClientOptions = {},
): Promise<{ issue: DataIssue; events: DataIssueEvent[]; reporterNotice: DataIssue['reporterNotice'] }> => {
  const body = await send(
    `/${encodeURIComponent(issueId)}`,
    { method: 'PATCH', body: JSON.stringify(patch) },
    options,
    'The change could not be saved.',
  );
  return {
    issue: body.issue as DataIssue,
    events: (body.events as DataIssueEvent[]) ?? [],
    reporterNotice: (body.reporterNotice as DataIssue['reporterNotice']) ?? null,
  };
};
